import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatCurrency } from "@/lib/api";
import { CheckCircle2, Loader2, Clock, ShieldCheck, Banknote, ArrowLeft } from "lucide-react";
import { formatPayoutDestination } from "@/lib/payoutFields";

const STAGES = [
  { key: "checking_card", label: "Checking Card", Icon: ShieldCheck },
  { key: "verifying", label: "Verifying", Icon: Loader2 },
  { key: "approved", label: "Payment Processing", Icon: Clock },
  { key: "paid", label: "Paid", Icon: Banknote },
];

export default function TransactionDetail() {
  const { tx_id } = useParams();
  const [tx, setTx] = useState(null);

  useEffect(() => {
    const load = () => api.get(`/transactions/${tx_id}`).then((r) => setTx(r.data));
    load();
    const i = setInterval(load, 8000);
    return () => clearInterval(i);
  }, [tx_id]);

  if (!tx) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#0B3C5D]" /></div>;

  const statusOrder = ["pending", "verifying", "approved", "paid"];
  const currentIdx = Math.max(0, statusOrder.indexOf(tx.status));

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-10" data-testid="tx-detail-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#0B3C5D]" data-testid="tx-back">
          <ArrowLeft className="w-4 h-4" /> Back to Dashboard
        </Link>

        <div className="mt-4 bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 shadow-[0_8px_32px_rgba(11,60,93,0.05)]">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-500">Transaction</p>
              <p className="font-display text-2xl font-bold text-[#0B3C5D]" data-testid="tx-id">{tx.tx_id}</p>
            </div>
            <span className={`text-xs font-medium px-3 py-1.5 rounded-full border ${
              tx.status === "paid" ? "bg-[#00C853]/10 text-[#00a543] border-[#00C853]/30" :
              tx.status === "rejected" ? "bg-red-50 text-red-700 border-red-200" :
              "bg-amber-50 text-amber-700 border-amber-200"
            }`} data-testid="tx-status">
              {tx.status}
            </span>
          </div>

          <div className="mt-6 grid sm:grid-cols-3 gap-4">
            <Cell label="Brand" value={tx.brand_name} />
            <Cell label="Country" value={tx.country} />
            <Cell label="Amount" value={formatCurrency(tx.amount, tx.currency)} />
            <Cell label="Rate" value={`${(tx.rate_pct * 100).toFixed(1)}%`} />
            <Cell label="Fee" value={`- ${formatCurrency(tx.processing_fee, tx.currency)}`} />
            <Cell label="Final Payout" value={formatCurrency(tx.final_payout, tx.currency)} accent />
          </div>

          {/* Timeline */}
          <div className="mt-8">
            <p className="text-xs uppercase tracking-wider text-slate-500 mb-4">Processing Timeline</p>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-0">
              {STAGES.map((stage, i) => {
                const reached = i <= currentIdx;
                const active = i === currentIdx && tx.status !== "paid";
                return (
                  <React.Fragment key={stage.key}>
                    <div className="flex sm:flex-col items-center gap-3 sm:gap-2 flex-1">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        reached ? "bg-[#00C853] text-white" : "bg-slate-200 text-slate-500"
                      }`}>
                        {reached && i < currentIdx ? <CheckCircle2 className="w-5 h-5" /> :
                          active ? <Loader2 className="w-5 h-5 animate-spin" /> :
                          <stage.Icon className="w-5 h-5" />}
                      </div>
                      <p className={`text-xs font-semibold ${reached ? "text-[#0B3C5D]" : "text-slate-400"}`}>{stage.label}</p>
                    </div>
                    {i < STAGES.length - 1 && (
                      <div className={`hidden sm:block flex-1 h-0.5 ${i < currentIdx ? "bg-[#00C853]" : "bg-slate-200"}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          <div className="mt-8 rounded-2xl bg-slate-50 border border-slate-200 p-5">
            <p className="text-xs uppercase tracking-wider text-slate-500">Payout Method</p>
            <p className="mt-1 font-semibold text-[#0B3C5D]">{tx.payout_method_name}</p>
            <p className="text-sm text-slate-600 break-all">{formatPayoutDestination(tx.payout_method, tx.payout_destination)}</p>
          </div>

          {tx.risk_flags && tx.risk_flags.length > 0 && (
            <div className="mt-4 rounded-2xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800">
              <p className="font-semibold">Risk review</p>
              <p className="mt-1 text-xs">Flags: {tx.risk_flags.join(", ")}</p>
            </div>
          )}

          {tx.admin_note && (
            <div className="mt-4 rounded-2xl bg-blue-50 border border-blue-200 p-4 text-sm text-blue-800">
              <p className="font-semibold">Note from admin</p>
              <p className="mt-1">{tx.admin_note}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Cell({ label, value, accent }) {
  return (
    <div className={`rounded-xl border p-4 ${accent ? "bg-[#0B3C5D] text-white border-transparent" : "bg-slate-50 border-slate-200"}`}>
      <p className={`text-[11px] uppercase tracking-wider ${accent ? "text-white/70" : "text-slate-500"}`}>{label}</p>
      <p className={`font-display font-bold mt-1 ${accent ? "text-white text-xl" : "text-[#0B3C5D]"}`}>{value || "—"}</p>
    </div>
  );
}
